import type {
    Extension,
    CreateExtensionArgument,
    DmApis,
    ResourceLoaderDefinition,
    ResourceLoaderArgs,
    ModelsInitializationType,
    DmStore
} from '@wix/document-manager-core'
import type {FragmentListItem, RMTopology} from '@wix/document-services-types'
import type {
    ListFragmentsMetadataResponse,
    GetFragmentResponse,
    FragmentTypeAndName,
    FragmentMetadata,
    Fragment,
    NamespaceData,
    DataItem
} from '@wix/ambassador-document-management-fragments-v1-fragment-snapshot/types'
import {type Resource, RESOURCE_TYPES} from '../../constants/constants'
import type {DocumentServicesModelExtApi} from '../documentServicesModel'
import _ from 'lodash'
import type {FragmentsExtensionAPI} from '../fragments/fragments'
import {mapGetFragmentsDataResponse, mapGetFragmentsStaticDataResponse} from './fragmentLoaderUtils'
import type {CSaveApi} from '../csave/continuousSave'

interface FragmentServer {
    listFragments(branchId?: string): Promise<ListFragmentsMetadataResponse>
    getFragmentData(fragmentTypeAndName: FragmentTypeAndName, branchId?: string): Promise<GetFragmentResponse>
    getFragmentsData(fragmentTypeAndNames: FragmentTypeAndName[], branchId?: string): Promise<GetFragmentResponse[]>
}

const INTERACTIONS = {
    DYNAMIC_FRAGMENT_LOADER: 'load-dynamic-fragment',
    STATIC_FRAGMENT_LOADER: 'static-fragment-loader'
}

const createExtension = ({environmentContext, dsConfig, logger}: CreateExtensionArgument): Extension => {
    let fragmentsToLoad: FragmentListItem[] = []
    let pageTopology: RMTopology[]

    const listFragments = async (branchId?: string): Promise<ListFragmentsMetadataResponse> => {
        return await environmentContext.serverFacade.listFragments(branchId)
    }

    const getFragmentsData = async (
        fragmentTypeAndNames: FragmentTypeAndName[],
        branchId?: string
    ): Promise<Fragment[]> => {
        return (await environmentContext.serverFacade.getFragmentsData(fragmentTypeAndNames, branchId))
            .map((res: GetFragmentResponse) => res.fragment)
            .filter(Boolean)
            .map(mapGetFragmentsDataResponse)
    }

    const setFragmentsToDal = (dmAPIs: DmApis, fragments: Fragment[]) => {
        if (!fragments.length) {
            return
        }
        const {fragments: fragmentsExtension} = dmAPIs.extensionAPI as FragmentsExtensionAPI
        _.forEach(fragments, fragment => {
            if (fragment.name && fragment.type) {
                fragmentsExtension.mergeFragment(fragment)
            }
        })
    }

    const setFragmentsIndexesToDal = (dmAPIs: DmApis, fragments: Fragment[]) => {
        if (!fragments.length) {
            return
        }
        const {fragments: fragmentsExtension} = dmAPIs.extensionAPI as FragmentsExtensionAPI
        const indexFragment = _.find(fragments, f => f?.type === 'FragmentIndex' && !!f.content)
        if (!indexFragment?.content) {
            return
        }
        const {content} = indexFragment
        _.forEach(content, (namespaceData: NamespaceData, namespaceName: string) => {
            const namespaceItems = namespaceData?.namespaceItems
            if (!namespaceItems) {
                return
            }
            _.forEach(namespaceItems, (itemData: DataItem, itemKey: string) => {
                const itemId = itemData?.id ?? itemKey
                if (!itemId) {
                    return
                }
                fragmentsExtension.mergeFragmentIndex(
                    {
                        name: itemId,
                        type: namespaceName
                    },
                    itemData.value
                )
            })
        })
    }

    const createResourceLoaderDefinition = (
        fragmentType: Resource,
        args: ResourceLoaderArgs
    ): ResourceLoaderDefinition => {
        const fetchFragments = async (fragments: FragmentListItem[]) => {
            const urlFallbacksTemplates = pageTopology.map(
                urlDescriptor => `${urlDescriptor.baseUrl}${urlDescriptor.parts}`
            )
            const resolvedFragmentsPagesPromise = await environmentContext.fetchFragmentsFacade!.fetchFragments(
                environmentContext.fetchFn,
                fragments,
                urlFallbacksTemplates,
                100,
                environmentContext.fragmentFetchFallbackFn
            )

            return resolvedFragmentsPagesPromise
        }

        const loadDataFromTransactions = async (dmAPIs: DmApis) => {
            const {continuousSave} = dmAPIs.extensionAPI as CSaveApi
            if (continuousSave?.initFragmentsTransactions) {
                try {
                    await continuousSave.initFragmentsTransactions()
                } catch (error) {
                    console.error('Error initializing fragments transactions', error)
                    throw error
                }
            }
        }

        const load = async (resourcesToLoad?: FragmentMetadata[]) => {
            if (resourcesToLoad?.length) {
                logger.interactionStarted(INTERACTIONS.DYNAMIC_FRAGMENT_LOADER, {
                    extras: {
                        fragmentsToLoad: JSON.stringify(resourcesToLoad.map(fragment => fragment.type))
                    }
                })
                const fragmentsData: Fragment[] = await getFragmentsData(
                    resourcesToLoad.map(fragmentResource => ({
                        type: fragmentResource.type,
                        name: fragmentResource.name
                    }))
                )
                setFragmentsToDal(args, fragmentsData)
            } else {
                if (fragmentsToLoad.length) {
                    logger.interactionStarted(INTERACTIONS.STATIC_FRAGMENT_LOADER, {
                        extras: {
                            fragmentsToLoad: JSON.stringify(fragmentsToLoad.map(fragment => fragment.fragmentType))
                        }
                    })
                    const rawFragmentsData = await fetchFragments(fragmentsToLoad)
                    const fragmentsData: Fragment[] = rawFragmentsData.map(mapGetFragmentsStaticDataResponse)

                    const [fragmentsIndexesData, fragmentsDataWithoutIndexes] = _.partition(
                        fragmentsData,
                        fragment => fragment.type === 'FragmentIndex'
                    )
                    setFragmentsIndexesToDal(args, fragmentsIndexesData)
                    setFragmentsToDal(args, fragmentsDataWithoutIndexes)
                    logger.interactionEnded(INTERACTIONS.STATIC_FRAGMENT_LOADER)
                }

                await loadDataFromTransactions(args)
            }
        }

        const loadAll = async (dynamicFragmentsToLoad?: Record<string, Map<string, FragmentMetadata>>) => {
            if (dynamicFragmentsToLoad?.[fragmentType]) {
                return load(Array.from(dynamicFragmentsToLoad[fragmentType].values()))
            }
            return load()
        }

        const getFragmentList = async (): Promise<FragmentMetadata[] | undefined> => {
            const {siteAPI} = args.extensionAPI as DocumentServicesModelExtApi
            const {fragments} = await listFragments(siteAPI.getBranchId())
            return fragments
        }

        const getAvailableResources = () => {
            return fragmentsToLoad.map(fragment => fragment.fragmentType)
        }

        const initAvailableResources = async () => {
            if (dsConfig.disableFragments) {
                return
            }
            if (fragmentType === RESOURCE_TYPES.FRAGMENT_LOAD_ON_INIT) {
                await loadAll() //loading all default fragments
            } else if (!dsConfig.supportedFragments?.[fragmentType]?.loadOnInit) {
                const dynamicFragmentsToLoad: Record<string, Map<string, FragmentMetadata>> = {}
                //For fragments that we don't need on load, we need to load them by their singleton name or by getting the list of fragment names
                const fragmentSingletonName = dsConfig.supportedFragments?.[fragmentType]?.singletonInstance
                if (fragmentSingletonName) {
                    const fragment = {
                        name: fragmentSingletonName,
                        type: fragmentType
                    }
                    if (!dynamicFragmentsToLoad[fragment.type]) {
                        dynamicFragmentsToLoad[fragment.type] = new Map()
                    }
                    dynamicFragmentsToLoad[fragment.type].set(fragment.name, fragment)
                } else {
                    const fragments = await getFragmentList()
                    fragments?.forEach(fragment => {
                        if (!fragment.type || !fragment.name) {
                            return
                        }
                        if (!dsConfig.supportedFragments?.[fragment.type]) {
                            return
                        }
                        if (!dynamicFragmentsToLoad[fragment.type]) {
                            dynamicFragmentsToLoad[fragment.type] = new Map()
                        }
                        dynamicFragmentsToLoad[fragment.type].set(fragment.name, fragment)
                    })
                }
                await loadAll(dynamicFragmentsToLoad)
            }
        }

        return {
            resourceType: fragmentType,
            load: (fragments: any[]) => load(fragments),
            loadAll,
            getAvailableResources,
            initAvailableResources
        }
    }

    const createResourceLoader = (args: ResourceLoaderArgs): ResourceLoaderDefinition[] => {
        const resourceLoaderDefinitions = [createResourceLoaderDefinition(RESOURCE_TYPES.FRAGMENT_LOAD_ON_INIT, args)]
        //TODO-fragments - Support dynamic loading of fragments that we don't need on load
        return resourceLoaderDefinitions
    }
    const initializeModels = (initialStore: DmStore, initialModels: ModelsInitializationType) => {
        const {fragmentsList} = initialModels.documentServicesModel
        const {pageList} = initialModels.rendererModel
        const {topology} = pageList

        pageTopology = topology

        if (fragmentsList?.length) {
            fragmentsToLoad = fragmentsList.filter(
                (fragment: FragmentListItem) =>
                    dsConfig.supportedFragments?.[fragment.fragmentType]?.loadOnInit === true
            )
        }
    }

    return {
        name: 'fragmentResourceLoader',
        initializeModels,
        createResourceLoader
    }
}

export {createExtension, FragmentServer}
